import { AccountRoles, PaymentMethods } from '../types/enums';
import { StandardParamsToODataParams } from '../types/interfaces';
import type { PaymentDetails } from '../types/interfaces/paymentTransaction.interfaces';
import { PaymentMethod } from '../types/interfaces/paymentMethod.interface';
import { getAccountById } from '../api/accountApi';
import {
  getPaymentDetailsByAccountId,
  getPaymentTransactions,
  getPaymentTransactionsOdata,
  getSubscriptionStatus,
} from '../api/paymentGatewayApi';
import { showToast } from './toast.service';
import { toastMessages } from '../constants/errorMessages';

export const doesUserHaveSettingAccess = async (accountId: number, userId: number) => {
  try {
    const { data: accountResponse } = await getAccountById(accountId, { include: 'Users.Roles' });
    const accountUser = accountResponse.users.find(accountUser => accountUser.userId === userId);

    return accountUser?.roles.some(role => role.name === AccountRoles.AccountOwner || role.name === AccountRoles.AccountAdmin);
  } catch (error: any) {
    throw error;
  }
};

export const doesAccountHasPendingPaymentTransaction = async (accountId?: number) => {
  try {
    if (accountId) {
      const params = { filter: `accountId eq ${accountId}`, sort: '^billedDate' };
      const { data: paymentHistoryResponse } = await getPaymentTransactionsOdata(StandardParamsToODataParams(params));
      return paymentHistoryResponse?.findIndex(paymentHistory => paymentHistory?.status === 'Pending') >= 0;
    } else {
      const params = { sort: '^billedDate' };
      const { data: paymentHistoryResponse } = await getPaymentTransactions(params);
      return paymentHistoryResponse?.findIndex(paymentHistory => paymentHistory?.status === 'Pending') >= 0;
    }
  } catch (error: any) {
    throw error;
  }
};

export const selectPrimaryPaymentDetailRow = (
  paymentDetailsResponse: PaymentDetails | PaymentDetails[] | null | undefined,
): PaymentDetails | undefined => {
  if (paymentDetailsResponse == null) return undefined;
  const rows: PaymentDetails[] = Array.isArray(paymentDetailsResponse)
    ? paymentDetailsResponse
    : [paymentDetailsResponse];
  if (rows.length === 0) return undefined;

  const defaultCard = rows.find(paymentDetail => paymentDetail.paymentMethod === PaymentMethods.CARD && paymentDetail.isDefault);
  if (defaultCard) return defaultCard;

  const anyCard = rows.find(paymentDetail => paymentDetail.paymentMethod === PaymentMethods.CARD);
  if (anyCard) return anyCard;

  const defaultAch = rows.find(paymentDetail => paymentDetail.paymentMethod === PaymentMethods.ACH && paymentDetail.isDefault);
  if (defaultAch) return defaultAch;

  const anyAch = rows.find(paymentDetail => paymentDetail.paymentMethod === PaymentMethods.ACH);
  if (anyAch) return anyAch;

  return rows[0];
};

export const getPaymentDetails = async (accountId: number, isUserAdmin?: boolean) => {
  try {
    const { data: isUserSubscribed } = await getSubscriptionStatus(accountId);
    if (isUserSubscribed) {
      const { data: paymentDetailsResponse } = await getPaymentDetailsByAccountId(accountId, isUserAdmin);
      const selected = selectPrimaryPaymentDetailRow(paymentDetailsResponse);
      if (selected == null) return undefined;
      const {
        paymentMethod,
        maskedCardNumber,
        expiry,
        accountType,
        isDefault,
        name,
        city,
        country,
        region,
        address,
        postal,
      } = selected;
      let accountData =
        paymentMethod === PaymentMethods.CARD
          ? {
              cardNumber: { value: maskedCardNumber, valid: maskedCardNumber?.length > 0 ? true : false },
              expiry: { value: expiry, valid: expiry?.length > 0 ? true : false },
              accountNumber: { value: '', valid: false },
              accountType: { value: '', name: '' },
            }
          : {
              cardNumber: { value: '', valid: false },
              expiry: { value: '', valid: false },
              accountNumber: { value: maskedCardNumber, valid: maskedCardNumber?.length > 0 ? true : false },
              accountType: { value: accountType, name: accountType },
            };

      return {
        paymentType: { value: paymentMethod, valid: paymentMethod ? true : false },
        name: { value: name, valid: name?.length > 0 ? true : false },
        address: { value: address, valid: address?.length > 0 ? true : false },
        city: { value: city, valid: city?.length > 0 ? true : false },
        region: { value: region, valid: region?.length > 0 ? true : false },
        postalCode: { value: postal, valid: postal?.length > 0 ? true : false },
        country: { value: country, valid: country?.length > 0 ? true : false },
        routingNumber: { value: '', valid: false },
        cvv: { value: '', valid: false },
        ...accountData,
      };
    }
  } catch {
    showToast.error(toastMessages.SOMETHING_WENT_WRONG);
  }
};

type MapPaymentInfoOptions = {
  isUpdate?: boolean;
};

const accountValueLooksMasked = (value: string | undefined): boolean => Boolean(value && /X/i.test(value));

export const mapPaymentInfo = (paymentMethod: PaymentMethod, options?: MapPaymentInfoOptions) => {
  const { address, city, region, postalCode, country, cardNumber, expiry, cvv, accountType, accountNumber, routingNumber } =
    paymentMethod;

  const isUpdate = options?.isUpdate === true;
  const isCard = paymentMethod?.paymentType?.value === PaymentMethods.CARD;

  const name = paymentMethod?.name?.value;
  const line1 = address?.value;
  const cityVal = city?.value;
  const regionVal = region?.value;
  const postalVal = postalCode?.value;
  const countryVal = country?.value;

  if (isCard) {
    return {
      paymentMethod: paymentMethod?.paymentType?.value,
      name,
      address: line1,
      city: cityVal,
      region: regionVal,
      postal: postalVal,
      country: countryVal,
      account: cardNumber?.value,
      expiry: expiry?.value,
      cvv2: cvv?.value,
    };
  }

  const maskedInstrumentUpdate =
    isUpdate &&
    paymentMethod.usesExistingMaskedAccount === true &&
    accountValueLooksMasked(accountNumber?.value);

  const accountPayload = maskedInstrumentUpdate
    ? {
        accountType: accountType?.value,
        ...(routingNumber?.value ? { bankRoutingNumber: routingNumber.value } : {}),
      }
    : {
        account: accountNumber?.value,
        bankRoutingNumber: routingNumber?.value,
        accountType: accountType?.value,
      };

  const profilePayload =
    paymentMethod.profileId != null && String(paymentMethod.profileId).length > 0
      ? { profileId: paymentMethod.profileId }
      : {};

  return {
    paymentMethod: paymentMethod?.paymentType?.value,
    ...profilePayload,
    name,
    address: line1,
    city: cityVal,
    region: regionVal,
    postal: postalVal,
    country: countryVal,
    expiry: expiry?.value ?? '',
    cvv2: cvv?.value ?? '',
    ...accountPayload,
  };
};
